import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Shield, UserPlus, X, Check, Plus, Star, Users, Tag, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function StaffPanel({ eventId, staff, savedProfiles, onRefresh }) {
  const [showStaffForm, setShowStaffForm] = useState(false);
  const [staffEmail, setStaffEmail] = useState('');
  const [addingStaff, setAddingStaff] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [rosterForm, setRosterForm] = useState({ first_name: '', last_name: '', nickname: '', email: '', is_default_door_team: false });
  const [editingProfile, setEditingProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  const resetRosterForm = () => setRosterForm({ first_name: '', last_name: '', nickname: '', email: '', is_default_door_team: false });

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setAddingStaff(true);
    try {
      const token = localStorage.getItem('session_token');
      const r = await axios.post(`${API}/events/${eventId}/staff`, { email: staffEmail }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${r.data.name} added`);
      setStaffEmail('');
      setShowStaffForm(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setAddingStaff(false); }
  };

  const handleRemoveStaff = async (userId) => {
    if (!confirm('Remove staff?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/events/${eventId}/staff/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Removed');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleQuickAssign = async (profile) => {
    setAddingStaff(true);
    try {
      const token = localStorage.getItem('session_token');
      const r = await axios.post(`${API}/events/${eventId}/staff`, { email: profile.email }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${r.data.display_name || r.data.name} assigned`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to assign'); }
    finally { setAddingStaff(false); }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = { Authorization: `Bearer ${token}` };
      if (editingProfile) {
        await axios.put(`${API}/staff-profiles/${editingProfile}`, rosterForm, { headers });
        toast.success('Profile updated');
      } else {
        await axios.post(`${API}/staff-profiles`, rosterForm, { headers });
        toast.success('Profile saved');
      }
      resetRosterForm();
      setEditingProfile(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingProfile(false); }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!confirm('Delete this staff profile?')) return;
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/staff-profiles/${profileId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Profile deleted');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleToggleDefaultTeam = async (profileId, currentValue) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/staff-profiles/${profileId}`, { is_default_door_team: !currentValue }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(!currentValue ? 'Added to default door team' : 'Removed from default door team');
      onRefresh();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <>
      {/* Check-In Staff */}
      <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm" data-testid="check-in-staff-section">
        <div className="p-4 border-b border-[#1A1A1A] flex flex-wrap justify-between items-center gap-2">
          <div>
            <h2 className="text-lg font-semibold text-[#EAE7E1] flex items-center"><Shield className="w-5 h-5 mr-2 text-[#D4AF37]" />Check-In Staff</h2>
            <p className="text-xs text-[#777] mt-1">{staff.length}/6 assigned</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowRoster(!showRoster)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm text-xs" data-testid="manage-roster-button">
              {showRoster ? 'Hide Roster' : 'Manage Roster'}
            </Button>
            <Button onClick={() => setShowStaffForm(!showStaffForm)} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm text-xs" disabled={staff.length >= 6} data-testid="add-staff-button">
              <UserPlus className="w-3 h-3 mr-1" />Add by Email
            </Button>
          </div>
        </div>

        {/* Add by email form */}
        {showStaffForm && (
          <div className="p-4 border-b border-[#1A1A1A] bg-[#050505]">
            <form onSubmit={handleAddStaff} className="flex flex-col sm:flex-row gap-2">
              <Input type="email" value={staffEmail} onChange={(e) => setStaffEmail(e.target.value)} placeholder="Staff email (must have account)" className="flex-1" required data-testid="staff-email-input" />
              <Button type="submit" disabled={addingStaff} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm px-6 font-bold" data-testid="confirm-add-staff">{addingStaff ? 'Adding...' : 'Add'}</Button>
            </form>
            <p className="text-xs text-[#777] mt-2">Staff can scan QR codes and check in guests but cannot edit events.</p>
          </div>
        )}

        {/* Quick Assign from Saved Roster */}
        {!showStaffForm && savedProfiles.length > 0 && staff.length < 6 && (
          <div className="p-4 border-b border-[#1A1A1A] bg-[#050505]" data-testid="quick-assign-panel">
            <div className="text-xs font-medium text-[#777] uppercase tracking-wide mb-2">Quick Assign from Roster</div>
            <div className="flex flex-wrap gap-2">
              {savedProfiles.map(p => {
                const isAssigned = staff.some(s => s.email === p.email);
                const displayName = p.nickname || `${p.first_name} ${p.last_name}`.trim();
                return (
                  <button
                    key={p.profile_id}
                    type="button"
                    disabled={isAssigned || addingStaff}
                    onClick={() => handleQuickAssign(p)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-medium transition-all ${
                      isAssigned
                        ? 'border-green-900/40 bg-green-900/10 text-green-400 cursor-default'
                        : 'border-[#1A1A1A] text-[#AFAFAF] hover:border-[#C6A66B] hover:text-[#C6A66B] cursor-pointer'
                    }`}
                    data-testid={`quick-assign-${p.profile_id}`}
                  >
                    {isAssigned ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
                    {p.is_default_door_team && <Star className="w-3 h-3 fill-[#D4AF37] text-[#D4AF37]" />}
                    {displayName}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Currently Assigned Staff */}
        {staff.length === 0 ? (
          <div className="p-6 text-center text-[#555] text-sm">No staff assigned. {savedProfiles.length > 0 ? 'Use Quick Assign above or add by email.' : 'Add staff by email or create a staff roster.'}</div>
        ) : (
          <div className="divide-y divide-[#1A1A1A]">
            {staff.map(s => {
              const displayName = s.display_name || s.name || s.email;
              return (
                <div key={s.user_id} className="px-4 py-3 flex items-center justify-between" data-testid={`staff-${s.user_id}`}>
                  <div>
                    <div className="text-sm font-medium text-[#EAE7E1]">{displayName}</div>
                    <div className="text-xs text-[#555]">{s.email}</div>
                  </div>
                  <Button onClick={() => handleRemoveStaff(s.user_id)} variant="ghost" size="sm" className="text-[#555] hover:text-[#C44545]" data-testid={`remove-staff-${s.user_id}`}><X className="w-4 h-4" /></Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Saved Staff Roster */}
      {showRoster && (
        <div className="bg-[#0A0A0A] border border-[#C6A66B]/20 rounded-sm" data-testid="staff-roster-panel">
          <div className="p-4 border-b border-[#1A1A1A]">
            <h2 className="text-lg font-semibold text-[#EAE7E1] flex items-center gap-2"><Users className="w-5 h-5 text-[#D4AF37]" /> Saved Staff Roster</h2>
            <p className="text-xs text-[#777] mt-1">Save staff profiles once, reuse across all your events.</p>
          </div>
          <div className="p-4 border-b border-[#1A1A1A] bg-[#050505]">
            <form onSubmit={handleSaveProfile} className="space-y-3" data-testid="roster-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#EAE7E1] text-xs">First Name *</Label>
                  <Input value={rosterForm.first_name} onChange={e => setRosterForm(f => ({ ...f, first_name: e.target.value }))} required className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="roster-first-name" />
                </div>
                <div>
                  <Label className="text-[#EAE7E1] text-xs">Last Name *</Label>
                  <Input value={rosterForm.last_name} onChange={e => setRosterForm(f => ({ ...f, last_name: e.target.value }))} required className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="roster-last-name" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[#EAE7E1] text-xs">Nickname <span className="text-[#555]">(optional)</span></Label>
                  <Input value={rosterForm.nickname} onChange={e => setRosterForm(f => ({ ...f, nickname: e.target.value }))} className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" placeholder="e.g. JM, Big Mike" data-testid="roster-nickname" />
                </div>
                <div>
                  <Label className="text-[#EAE7E1] text-xs">Email *</Label>
                  <Input type="email" value={rosterForm.email} onChange={e => setRosterForm(f => ({ ...f, email: e.target.value }))} required className="mt-1 bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="roster-email" />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="roster-default-team-toggle">
                <input
                  type="checkbox"
                  checked={rosterForm.is_default_door_team}
                  onChange={e => setRosterForm(f => ({ ...f, is_default_door_team: e.target.checked }))}
                  className="rounded border-[#1A1A1A]"
                />
                <span className="text-xs text-[#AFAFAF]">Add to default door team</span>
                <Star className={`w-3 h-3 ${rosterForm.is_default_door_team ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-[#555]'}`} />
              </label>
              <div className="flex gap-2">
                <Button type="submit" disabled={savingProfile} className="bg-[#D4AF37] hover:bg-[#F2D27A] text-[#050505] rounded-sm font-bold" data-testid="save-profile-button">
                  {savingProfile ? 'Saving...' : editingProfile ? 'Update Profile' : 'Save to Roster'}
                </Button>
                {editingProfile && (
                  <Button type="button" onClick={() => { setEditingProfile(null); resetRosterForm(); }} variant="outline" className="border-[#1A1A1A] text-[#777]">Cancel</Button>
                )}
              </div>
            </form>
          </div>
          {savedProfiles.length === 0 ? (
            <div className="p-6 text-center text-[#555] text-sm">No saved staff yet. Add your first team member above.</div>
          ) : (
            <div className="divide-y divide-[#1A1A1A]">
              {savedProfiles.map(p => {
                const displayName = p.nickname || `${p.first_name} ${p.last_name}`.trim();
                const isAssigned = staff.some(s => s.email === p.email);
                return (
                  <div key={p.profile_id} className="px-4 py-3 flex items-center justify-between gap-2" data-testid={`profile-${p.profile_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#EAE7E1]">
                        {displayName}
                        {p.nickname && <span className="text-xs text-[#555] ml-2">({p.first_name} {p.last_name})</span>}
                      </div>
                      <div className="text-xs text-[#555]">{p.email}{p.user_id ? '' : ' (no account yet)'}</div>
                    </div>
                    <div className="flex gap-1 flex-shrink-0 items-center">
                      {isAssigned && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-green-900/20 text-green-400">Assigned</span>}
                      <button
                        type="button"
                        onClick={() => handleToggleDefaultTeam(p.profile_id, !!p.is_default_door_team)}
                        className={`p-1.5 rounded-sm transition-all ${p.is_default_door_team ? 'text-[#D4AF37] hover:text-[#F2D27A]' : 'text-[#333] hover:text-[#555]'}`}
                        title={p.is_default_door_team ? 'Remove from default door team' : 'Add to default door team'}
                        data-testid={`toggle-default-${p.profile_id}`}
                      >
                        <Star className={`w-3.5 h-3.5 ${p.is_default_door_team ? 'fill-[#D4AF37]' : ''}`} />
                      </button>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => { setEditingProfile(p.profile_id); setRosterForm({ first_name: p.first_name, last_name: p.last_name, nickname: p.nickname || '', email: p.email, is_default_door_team: !!p.is_default_door_team }); }}
                        className="text-[#555] hover:text-[#AFAFAF]" data-testid={`edit-profile-${p.profile_id}`}
                      >
                        <Tag className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteProfile(p.profile_id)} className="text-[#555] hover:text-[#C44545]" data-testid={`delete-profile-${p.profile_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
